/**
 * FG Magnit — collection showcase.
 * Pointing at a collection name, or tabbing to it, swaps the image panel.
 * Everything is a plain link underneath, so the section still works with no JS:
 * the first panel is rendered active and each name goes to its collection.
 */
class FgShowcase extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('.fg-show__tab')];
    this.panels = [...this.querySelectorAll('.fg-show__panel')];
    this.panels.forEach((panel) => this.bindCards(panel));
    if (this.tabs.length < 2 || !this.panels.length) return;

    this.current = 0;
    this.pending = null;

    this.tabs.forEach((tab) => {
      const index = Number(tab.dataset.index);
      // A small delay stops panels flickering as the pointer crosses the list.
      tab.addEventListener('pointerenter', () => this.queue(index));
      tab.addEventListener('focus', () => this.show(index));
      tab.addEventListener('pointerleave', () => this.cancel());
    });
  }

  /**
   * The card under the pointer (or holding keyboard focus) is the wide one.
   * Tracking it here rather than with :hover means crossing the small gap
   * between two cards keeps the last card wide instead of flicking every card
   * back to compact for a frame; leaving the row returns them all to compact.
   */
  bindCards(panel) {
    const cells = [...panel.querySelectorAll('.fg-show__cell')];
    if (cells.length < 2) return;
    const expand = (cell) => cells.forEach((c) => c.classList.toggle('is-expanded', c === cell));

    panel.addEventListener('pointerover', (event) => {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      const cell = event.target.closest('.fg-show__cell');
      if (cell && !cell.classList.contains('is-expanded')) expand(cell);
    });
    panel.addEventListener('pointerleave', () => expand(null));
    panel.addEventListener('focusin', (event) => {
      const cell = event.target.closest('.fg-show__cell');
      if (cell) expand(cell);
    });
    panel.addEventListener('focusout', (event) => {
      if (!panel.contains(event.relatedTarget)) expand(null);
    });
  }

  disconnectedCallback() {
    this.cancel();
  }

  queue(index) {
    this.cancel();
    this.pending = setTimeout(() => this.show(index), 60);
  }

  cancel() {
    if (this.pending) {
      clearTimeout(this.pending);
      this.pending = null;
    }
  }

  show(index) {
    this.cancel();
    if (index === this.current || Number.isNaN(index)) return;
    this.current = index;

    this.tabs.forEach((tab) => {
      tab.classList.toggle('is-active', Number(tab.dataset.index) === index);
    });

    this.panels.forEach((panel) => {
      const active = Number(panel.dataset.index) === index;
      panel.classList.toggle('is-active', active);
      // Hidden panels stay out of the accessibility tree and the tab order.
      if (active) {
        panel.removeAttribute('aria-hidden');
      } else {
        panel.setAttribute('aria-hidden', 'true');
      }
    });
  }
}

if (!customElements.get('fg-showcase')) {
  customElements.define('fg-showcase', FgShowcase);
}
